import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, PasswordField } from '../components/ui';
import { useEnter } from '../lib/motion';

export function LoginPage({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [mode, setMode] = useState<'login' | 'bootstrap'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organization, setOrganization] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const cardRef = useEnter<HTMLDivElement>([mode]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') await api.login(email, password);
      else await api.bootstrap({ email, password, organization: organization || undefined });
      onAuthenticated();
      navigate('/sites', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div ref={cardRef} className="w-full max-w-md">
        <div className="mb-6 text-center">
          <img
            src="/brand/crumblemonster-256.webp"
            alt="Crumblemonster, das Maskottchen von ilovecookies"
            width={112}
            height={112}
            className="mx-auto mb-3 size-28"
            loading="eager"
            decoding="async"
          />
          <h1 className="text-2xl font-semibold tracking-tight">ilovecookies</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            {mode === 'login' ? 'Consent-Verwaltung für deine Websites.' : 'Erstes Konto anlegen.'}
          </p>
        </div>

        <Card>
          <form onSubmit={submit} className="flex flex-col gap-4">
            {error && <Alert tone="error">{error}</Alert>}

            <Field
              label="E-Mail"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <PasswordField
              label="Passwort"
              name="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={mode === 'bootstrap' ? 12 : undefined}
              hint={mode === 'bootstrap' ? 'Mindestens 12 Zeichen.' : undefined}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            {mode === 'bootstrap' && (
              <Field
                label="Name deiner Agentur"
                value={organization}
                placeholder="JIF Works"
                onChange={(event) => setOrganization(event.target.value)}
              />
            )}

            <Button type="submit" loading={busy} className="mt-1">
              {mode === 'login' ? 'Anmelden' : 'Konto anlegen'}
            </Button>
          </form>
        </Card>

        <p className="mt-4 text-center text-sm text-[var(--text-muted)]">
          {mode === 'login' ? (
            <>
              Noch kein Konto auf dieser Instanz?{' '}
              <button type="button" className="cursor-pointer font-medium underline" onClick={() => setMode('bootstrap')}>
                Erstes Konto anlegen
              </button>
            </>
          ) : (
            <button type="button" className="cursor-pointer font-medium underline" onClick={() => setMode('login')}>
              Zurück zur Anmeldung
            </button>
          )}
        </p>
      </div>
    </div>
  );
}
